import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowLeft, FolderOpen, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import DeleteConfirmModal from '../../components/DeleteConfirmModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResourceDetailPage = () => {
  const { resourceId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [resource, setResource] = useState(null);
  const [company, setCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    seniority: '',
    daily_rate: 0,
    availability_status: 'available'
  });

  useEffect(() => {
    fetchResourceDetail();
  }, [resourceId]);

  const fetchResourceDetail = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/resources/${resourceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResource(response.data.resource);
      setCompany(response.data.company);
      const res = response.data.resource;
      setFormData({
        name: res.name || `${res.first_name || ''} ${res.last_name || ''}`.trim(),
        role: res.role || '',
        seniority: res.seniority || '',
        daily_rate: res.daily_rate || 0,
        availability_status: res.availability_status || 'available'
      });
    } catch (error) {
      toast.error('Failed to load resource details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    setSaving(true);
    try {
      await axios.put(`${API}/admin/resources/${resourceId}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Resource updated successfully');
      fetchResourceDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update resource');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    const token = localStorage.getItem('token');
    
    try {
      await axios.delete(`${API}/admin/resources/${resourceId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { reason: 'Deleted from admin panel' }
      });
      toast.success('Resource deleted successfully');
      navigate('/admin/resources');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete resource');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#62D84E' }}></div>
      </div>
    );
  }

  if (!resource) {
    return (
      <div className="p-8">
        <p className="text-red-600">Resource not found</p>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <Button variant="ghost" onClick={() => navigate('/admin/resources')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Resources
          </Button>
          <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>Resource Details</h1>
          <p className="text-gray-600 mt-2">View and edit resource information</p>
        </div>
        <Button variant="outline" onClick={() => setShowDeleteModal(true)} className="text-red-600">
          <Trash2 className="w-4 h-4 mr-2" />
          Delete Resource
        </Button>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Resource"
        message={`Are you sure you want to delete ${resource.name}?`}
        entityName={resource.name}
        consequences={[
          'Resource will be marked as deleted',
          'Cannot delete if there are active reservations'
        ]}
        isDeleting={deleting}
      />

      {/* Edit Form */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Edit Resource</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
              />
            </div>
            <div>
              <Label>Role</Label>
              <Input
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
              />
            </div>
            <div>
              <Label>Seniority</Label>
              <select
                value={formData.seniority}
                onChange={(e) => setFormData({...formData, seniority: e.target.value})}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="junior">Junior</option>
                <option value="intermediate">Intermediate</option>
                <option value="senior">Senior</option>
              </select>
            </div>
            <div>
              <Label>Daily Rate</Label>
              <Input
                type="number"
                value={formData.daily_rate}
                onChange={(e) => setFormData({...formData, daily_rate: parseFloat(e.target.value)})}
              />
            </div>
            <div>
              <Label>Availability Status</Label>
              <select
                value={formData.availability_status}
                onChange={(e) => setFormData({...formData, availability_status: e.target.value})}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="available">Available</option>
                <option value="occupied">Occupied</option>
                <option value="idle">Idle</option>
              </select>
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={handleSave} disabled={saving} style={{ backgroundColor: '#62D84E' }}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Resource Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <FolderOpen className="w-5 h-5 mr-2" />
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-gray-600 text-sm">Resource ID</p>
              <p className="font-medium">{resource.resource_id}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Name</p>
              <p className="font-medium">{resource.name || `${resource.first_name || ''} ${resource.last_name || ''}`.trim() || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Role</p>
              <p className="font-medium">{resource.role || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Seniority</p>
              <p className="font-medium">{resource.seniority || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Status</p>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                resource.status === 'available' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {resource.status ? resource.status.toUpperCase() : 'N/A'}
              </span>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Activation Status</p>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                resource.is_activated ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {resource.is_activated ? 'ACTIVE' : 'INACTIVE'}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Additional Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-gray-600 text-sm">Company</p>
              <p className="font-medium">{company?.name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Day Rate</p>
              <p className="font-medium">${resource.day_rate?.toLocaleString() || 0}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Location</p>
              <p className="font-medium">{resource.location || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Languages</p>
              <p className="font-medium">{resource.languages?.join(', ') || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Certifications</p>
              <p className="font-medium">{resource.certifications?.join(', ') || 'None'}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Created At</p>
              <p className="font-medium">{new Date(resource.created_at).toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResourceDetailPage;